// Types and interfaces
interface VideoMetadata {
  mediaTime: number;
}

type VideoFrameRequestCallback = (now: DOMHighResTimeStamp, metadata: VideoMetadata) => void;

// Extend HTMLVideoElement to include non-standard properties
interface ExtendedHTMLVideoElement extends HTMLVideoElement {
  requestVideoFrameCallback?(callback: VideoFrameRequestCallback): number;

  mozPresentedFrames?: number;
  webkitDecodedFrameCount?: number;
}

// Configuration interface
interface FPSDetectionConfig {
  framesToMeasure: number;
  standardRates: number[];
}

class VideoFPSDetector {
  private readonly config: FPSDetectionConfig = {
    framesToMeasure: 5,
    standardRates: [ 23.976, 24, 25, 29.97, 30, 50, 59.94, 60 ]
  };

  private readonly videoElement: ExtendedHTMLVideoElement;

  constructor() {
    this.videoElement = document.createElement('video') as ExtendedHTMLVideoElement;
    this.videoElement.style.display = 'none';
    this.videoElement.muted = true;
    this.videoElement.volume = 0;
    // this.videoElement.playsInline = true;
    document.body.appendChild(this.videoElement);
  }

  private cleanup(videoURL: string | null = null): void {
    if (videoURL) {
      URL.revokeObjectURL(videoURL);
    }
    this.videoElement.src = '';
    this.videoElement.load();
  }

  private async detectFPSUsingFrameCallback(): Promise<number> {
    return new Promise<number>((resolve, reject) => {
      if (!this.videoElement.requestVideoFrameCallback) {
        reject(new Error('requestVideoFrameCallback not supported'));
        return;
      }

      let frameCount = 0;
      let lastTimestamp: number | null = null;
      let totalTimeDiff = 0;

      const frameCallback: VideoFrameRequestCallback = (now: DOMHighResTimeStamp, metadata: VideoMetadata) => {
        if (lastTimestamp !== null) {
          const timeDiff = metadata.mediaTime - lastTimestamp;
          if (timeDiff > 0) {
            totalTimeDiff += timeDiff;
            frameCount++;
          }
        }
        lastTimestamp = metadata.mediaTime;

        if (frameCount < this.config.framesToMeasure) {
          this.videoElement.requestVideoFrameCallback!(frameCallback);
        } else {
          const averageFrameTime = totalTimeDiff / frameCount;
          const fps = Math.round(1 / averageFrameTime);
          this.videoElement.pause();
          resolve(fps);
        }
      };

      this.videoElement.requestVideoFrameCallback!(frameCallback);
    });
  }

  private async detectFPSUsingMetadata(): Promise<number> {
    const frameCount = this.videoElement.mozPresentedFrames ||
      this.videoElement.webkitDecodedFrameCount;

    let detectedFPS: number;

    if (frameCount && this.videoElement.duration) {
      detectedFPS = frameCount / this.videoElement.duration;
    } else {
      const sampleDuration = Math.min(this.videoElement.duration, 1);
      detectedFPS = await new Promise<number>(resolve => {
        let frames = 0;
        const checkFrame = () => {
          frames++;
          if (this.videoElement.currentTime < sampleDuration) {
            this.videoElement.currentTime += 0.01;
            requestAnimationFrame(checkFrame);
          } else {
            resolve(frames / sampleDuration);
          }
        };
        this.videoElement.currentTime = 0;
        checkFrame();
      });
    }

    // Round to nearest standard rate
    return this.config.standardRates.reduce((prev, curr) =>
      Math.abs(curr - detectedFPS) < Math.abs(prev - detectedFPS) ? curr : prev
    );
  }

  public async getVideoFPS(file: File): Promise<number> {
    if (!file.type.startsWith('video/')) {
      throw new Error('Invalid file type: Please provide a video file');
    }

    return new Promise<number>((resolve, reject) => {
      const handleError = (error: Error): void => {
        this.cleanup();
        reject(error);
      };

      const onMetadataLoaded = async (): Promise<void> => {
        try {
          let fps: number;

          // Try primary method first
          if ('requestVideoFrameCallback' in HTMLVideoElement.prototype) {
            try {
              await this.videoElement.play();
              fps = await this.detectFPSUsingFrameCallback();
            } catch (playError) {
              console.warn('Frame callback method failed, falling back to metadata method');
              fps = await this.detectFPSUsingMetadata();
            }
          } else {
            fps = await this.detectFPSUsingMetadata();
          }

          this.cleanup(this.videoElement.src);
          resolve(Math.round(fps));
        } catch (error) {
          handleError(error as Error);
        }
      };

      try {
        this.videoElement.addEventListener('loadedmetadata', onMetadataLoaded);
        this.videoElement.addEventListener('error', (e) => handleError(new Error('Video loading error')));

        this.videoElement.src = URL.createObjectURL(file);
      } catch (error) {
        handleError(error as Error);
      }
    });
  }

  public destroy(): void {
    this.cleanup();
    this.videoElement.remove();
  }
}

export async function getVideoFPS(file: File): Promise<number> {
  const detector = new VideoFPSDetector();
  try {
    const fps = await detector.getVideoFPS(file);
    console.log(`Detected FPS: ${ fps }`);
    return fps;
  } catch (error) {
    console.error('Error detecting FPS:', error);
    throw error;
  } finally {
    detector.destroy(); // Clean up resources
  }
}

/**
 * Calculates the bitrate of a video file based on its size and duration
 * @param fileSize - The size of the file in bytes
 * @param durationInSeconds - The duration of the video in seconds
 * @returns The bitrate in bits per second (bps)
 */
function calculateBitrate(fileSize: number, durationInSeconds: number): number {
  // Convert file size from bytes to bits
  const fileSizeInBits = fileSize * 8;

  // Calculate bitrate in bits per second
  return fileSizeInBits / durationInSeconds;
}

/**
 * Gets formatted bitrate in common units (Kbps, Mbps)
 * @param bitrate - The bitrate in bits per second
 * @returns A string with the formatted bitrate and appropriate unit
 */
function getFormattedBitrate(bitrate: number): string {
  return `${ (bitrate / 1000).toFixed(2) } Kbps`;
}

export async function getVideoBitrate(file: File, durationInSeconds: number): Promise<{
  bitrateInBps: number;
  bitrateInKbps: number;
  formattedBitrate: string;
}> {
  const fileSize = file.size; // File size in bytes

  const bitrateInBps = calculateBitrate(fileSize, durationInSeconds);
  const bitrateInKbps = bitrateInBps / 1000;
  const formattedBitrate = getFormattedBitrate(bitrateInBps);

  return { bitrateInBps, bitrateInKbps, formattedBitrate };
}